/**
 * Configurazione degli "spazi di lavoro" per mestiere (Videomaker, Social Media Manager).
 *
 * Ogni spazio definisce:
 *  - la navigazione essenziale (poche voci, nell'ordine in cui si lavora)
 *  - le colonne della bacheca "Il mio lavoro", derivate dai campi reali di ped_righe
 *    (copy_ok, visual_ok, media_url, approvato_da, stato, stato_posting).
 *
 * Le funzioni di classificazione sono pure: una riga PED finisce in UNA sola colonna,
 * valutando le regole nell'ordine (la prima che corrisponde vince).
 */
import {
  Home, LayoutDashboard, FileText, Clock, HardDrive, CalendarDays, CheckSquare,
  Users, Send, TrendingUp, Clapperboard, Megaphone, LayoutGrid, BookOpen, Flame,
} from 'lucide-react';

export const WORKSPACE_INFO = {
  completo: {
    label: 'Completo',
    short: 'Tutto',
    Icon: LayoutGrid,
    desc: 'Tutte le sezioni di EdenFlow, come sempre.',
  },
  videomaker: {
    label: 'Videomaker',
    short: 'Video',
    Icon: Clapperboard,
    desc: 'Cosa girare e montare, file da consegnare, riprese in calendario.',
  },
  social: {
    label: 'Social Media Manager',
    short: 'Social',
    Icon: Megaphone,
    desc: 'Copy da scrivere, approvazioni, programmazione e pubblicazioni.',
  },
};

// Navigazione essenziale per spazio. Il resto resta raggiungibile da "Tutte le sezioni".
export const WORKSPACE_NAV = {
  videomaker: [
    { to: '/', icon: LayoutDashboard, label: 'Il mio lavoro' },
    { to: '/ped', icon: FileText, label: 'Piano Editoriale', match: ['/ped'] },
    { to: '/produzione', icon: Clapperboard, label: 'Produzione video' },
    { to: '/trend', icon: Flame, label: 'Trend' },
    { to: '/ped-storie', icon: Clock, label: 'Storie' },
    { to: '/client-vault', icon: HardDrive, label: 'File e asset' },
    { to: '/calendario', icon: CalendarDays, label: 'Riprese e calendario' },
    { to: '/task', icon: CheckSquare, label: 'Task' },
    { to: '/clienti-attivi', icon: Users, label: 'Clienti' },
  ],
  social: [
    { to: '/', icon: LayoutDashboard, label: 'Il mio lavoro' },
    { to: '/ped', icon: FileText, label: 'Piano Editoriale', match: ['/ped'] },
    { to: '/produzione', icon: Clapperboard, label: 'Produzione video' },
    { to: '/trend', icon: Flame, label: 'Trend' },
    { to: '/ped-storie', icon: Clock, label: 'Storie' },
    { to: '/pubblicazioni', icon: Send, label: 'Pubblicazioni' },
    { to: '/calendario', icon: CalendarDays, label: 'Calendario' },
    { to: '/task', icon: CheckSquare, label: 'Task' },
    { to: '/clienti-attivi', icon: Users, label: 'Clienti' },
    { to: '/analytics', icon: TrendingUp, label: 'Analytics', roles: ['admin','boss','social_manager'] },
    { to: '/guida', icon: BookOpen, label: 'Guida' },
  ],
};

// Voce per tornare alla panoramica classica quando la Home mostra la bacheca.
export const PANORAMICA_NAV = { to: '/panoramica', icon: Home, label: 'Panoramica' };

/* ── Helpers sui dati PED ─────────────────────────────────────────────── */

export function hasMedia(r) {
  const m = r?.media_url;
  if (!m) return false;
  if (Array.isArray(m)) return m.some(Boolean);
  if (typeof m === 'string') {
    try { const p = JSON.parse(m); return Array.isArray(p) ? p.some(Boolean) : !!p; } catch { return m !== '' && m !== '[]'; }
  }
  return true;
}

export function isApprovata(r) {
  return !!r?.approvato_da || r?.stato === 'Approvato' || r?.stato === 'Pubblicato';
}

export function isChiusa(r) {
  return r?.stato === 'Pubblicato' || r?.stato === 'Scartato' || r?.stato_posting === 'pubblicata';
}

/* ── Colonne della bacheca ────────────────────────────────────────────── */
// tone: cyan | yellow | purple | green | red | neutral (vedi Badge del kit UI)
// action: azione primaria rapida mostrata sulla card (gestita dalla pagina)

export const BOARD = {
  videomaker: [
    {
      key: 'produrre', title: 'Da produrre', tone: 'yellow',
      hint: 'Contenuti da girare, montare o disegnare.',
      match: (r) => !r.visual_ok,
      action: { kind: 'visual_ok', label: 'Visual pronto' },
    },
    {
      key: 'caricare', title: 'File da caricare', tone: 'purple',
      hint: 'Il visual è pronto ma il file non è ancora collegato al contenuto.',
      match: (r) => !hasMedia(r),
      action: { kind: 'open', label: 'Carica file' },
    },
    {
      key: 'revisione', title: 'In revisione', tone: 'cyan',
      hint: 'Consegnato: in attesa di copy o approvazione.',
      match: (r) => !isApprovata(r),
    },
    {
      key: 'consegnati', title: 'Consegnati', tone: 'green',
      hint: 'Approvati, pronti per la pubblicazione.',
      match: () => true,
    },
  ],
  social: [
    {
      key: 'risolvere', title: 'Da risolvere', tone: 'red',
      hint: 'Scaduti, pubblicazione fallita o modifiche chieste dal cliente.',
      match: (r) => r.stato_posting === 'da_risolvere' || r.cliente_appro === 'modifiche',
      action: { kind: 'open', label: 'Risolvi' },
    },
    {
      key: 'scrivere', title: 'Copy da scrivere', tone: 'yellow',
      hint: 'Caption, hook e testi ancora da completare.',
      match: (r) => !r.copy_ok,
      action: { kind: 'copy_ok', label: 'Copy pronto' },
    },
    {
      key: 'attesa', title: 'In attesa', tone: 'purple',
      hint: 'Aspettano il visual dal videomaker o l\'approvazione.',
      match: (r) => !r.visual_ok || !hasMedia(r) || !isApprovata(r),
    },
    {
      key: 'programmare', title: 'Da programmare', tone: 'cyan',
      hint: 'Tutto pronto: programma il post o preparati a pubblicarlo a mano.',
      match: (r) => r.stato_posting === 'pronta' || r.stato_posting === 'reminder' || r.stato_posting === 'bozza',
      action: { kind: 'open', label: 'Programma' },
    },
    {
      key: 'programmati', title: 'Programmati', tone: 'green',
      hint: 'In coda: usciranno da soli.',
      match: () => true,
    },
  ],
};

// Cosa manca a una riga dal punto di vista del mestiere (chip sulla card).
export function mancanze(ws, r) {
  const out = [];
  if (ws === 'videomaker') {
    if (!r.visual_ok) out.push('visual');
    if (!hasMedia(r)) out.push('file');
    if (!r.copy_ok) out.push('copy (SMM)');
  } else {
    if (!r.copy_ok) out.push('copy');
    if (!r.visual_ok) out.push('visual (video)');
    else if (!hasMedia(r)) out.push('file');
    if (!isApprovata(r)) out.push('approvazione');
    if (!r.data_pubblicazione) out.push('data');
    if (r.cliente_appro === 'modifiche') out.push('modifiche chieste dal cliente');
    for (const m of (r.posting_manca || [])) {
      if (m === 'profilo social') out.push('profilo social');
      else if (m.startsWith('pubblicazione fallita') || m.startsWith('scaduta')) out.push(m);
    }
  }
  return out;
}

export function classifica(ws, righe) {
  const cols = BOARD[ws] || [];
  const out = Object.fromEntries(cols.map(c => [c.key, []]));
  for (const r of righe) {
    if (isChiusa(r)) continue;
    const col = cols.find(c => c.match(r));
    if (col) out[col.key].push(r);
  }
  return out;
}
